import React, { useState, useEffect } from 'react';
import QuizQuestion from './QuizQuestion';
import QuizSummary from './QuizSummary';
import QuizProgress from './QuizProgress';

console.log("QuizForm component file loaded");

const quickFacts = [
  "AI can save coaches up to 15 hours per week on administrative tasks",
  "83% of successful coaches use integrated digital platforms",
  "Automated client communication increases retention by 40%",
  "Digital progress tracking improves client results by 35%"
];

const questions = [
  {
    id: 1,
    question: "What's your current client management process?",
    options: [
      "Manual spreadsheets and notes",
      "Basic CRM system",
      "Multiple disconnected tools",
      "Integrated digital platform"
    ]
  },
  {
    id: 2,
    question: "How do you currently handle client communication?",
    options: [
      "Email and phone only",
      "Basic messaging apps",
      "Multiple communication channels",
      "Integrated communication platform"
    ]
  },
  {
    id: 3,
    question: "What's your biggest challenge in scaling your coaching business?",
    options: [
      "Time management",
      "Client acquisition",
      "Service delivery",
      "Business operations"
    ]
  },
  {
    id: 4,
    question: "How do you currently track client progress?",
    options: [
      "Manual tracking",
      "Basic tracking tools",
      "Multiple tracking systems",
      "Integrated progress tracking"
    ]
  }
];

const QuizForm = ({ userData }) => {
  console.log("QuizForm rendering with userData:", userData);

  useEffect(() => {
    console.log("QuizForm mounted with userData:", userData);
  }, [userData]);

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [currentFact, setCurrentFact] = useState(0);

  useEffect(() => {
    if (loading) {
      const interval = setInterval(() => {
        setCurrentFact((prev) => (prev + 1) % quickFacts.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [loading]);

  const handleAnswer = (answer) => {
    console.log("Answer selected:", answer);
    setAnswers(prev => ({
      ...prev,
      [currentQuestion]: answer
    }));
  };

  const handleNext = () => {
    console.log("Next clicked, current question:", currentQuestion);
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const prompt = `As a wellness coach, I need help improving my business. Here are my details:
      Name: ${userData.name}
      Email: ${userData.email}
      Coaching Focus: ${userData.coachingFocus}
      
      Current practices:
      ${Object.entries(answers).map(([index, answer]) => 
        `${questions[index].question}: ${answer}`
      ).join('\n')}
      
      Please provide a concise analysis (max 300 words) with the following structure:
      1. Brief overview of current situation (1-2 sentences)
      2. 2-3 specific, actionable recommendations
      3. One clear next step to implement immediately
      
      Keep the response direct and practical. Focus on immediate actions rather than detailed explanations.`;

      const response = await fetch('https://kinos-api.onrender.com/api/coach/process', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt }),
      });

      const data = await response.json();
      setSummary(data.response);
    } catch (error) {
      console.error('Error submitting quiz:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-green-800 mb-4">Our AI Assistant is Analyzing Your Responses</h2>
          <p className="text-green-600 mb-8">We're crafting your personalized business improvement plan...</p>
          
          <div className="bg-green-50 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-green-800 mb-3">Did You Know?</h3>
            <p className="text-green-700 animate-fade-in-out">{quickFacts[currentFact]}</p>
          </div>
          
          <div className="flex justify-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-3 h-3 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-3 h-3 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (summary) {
    console.log("Rendering summary with:", summary);
    return <QuizSummary summary={summary} userData={userData} />;
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <QuizProgress current={currentQuestion + 1} total={questions.length} />
      <div className="bg-white rounded-xl shadow-lg p-6">
        <QuizQuestion
          question={questions[currentQuestion]}
          onAnswer={handleAnswer}
          selectedAnswer={answers[currentQuestion]}
          onNext={handleNext}
          isLastQuestion={currentQuestion === questions.length - 1}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default QuizForm; 